import { useState, useEffect, useCallback } from 'react';
import { reportsAPI } from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Loader2, ShieldAlert, Target, Award } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Badge } from '../components/ui/badge';

// 21 Oct 2026 (security follow-up, item 15): Daily Summary and Supplier
// Performance tabs are removed — both were built entirely from the retired
// `purchases` collection, which never produces new data.
export default function ReportsPage() {
  const { isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [catPmDays, setCatPmDays] = useState('30');
  const [catPmData, setCatPmData] = useState({ by_category: [], by_pm: [] });

  const fetchCatPmPerf = useCallback(async () => {
    try {
      const response = await reportsAPI.categoryPmPerformance(parseInt(catPmDays));
      setCatPmData(response.data);
    } catch (error) {
      toast.error('Failed to load category/PM performance');
    }
  }, [catPmDays]);

  useEffect(() => {
    if (!isAdmin) { setLoading(false); return; }
    setLoading(true);
    fetchCatPmPerf().finally(() => setLoading(false));
  }, [fetchCatPmPerf, isAdmin]);

  if (!isAdmin) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center" data-testid="reports-access-denied">
        <ShieldAlert className="h-12 w-12 text-muted-foreground/50 mb-3" />
        <h2 className="text-lg font-semibold">Reports is being rebuilt</h2>
        <p className="text-muted-foreground text-sm max-w-md mt-1">This page is temporarily limited to admins while it's rebuilt on real Zoho data.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Reports</h1>
        <p className="text-muted-foreground">Category and Product Manager performance, from Bid Engine results</p>
      </div>

      <Tabs defaultValue="category" className="space-y-6">
        <TabsList>
          <TabsTrigger value="category" data-testid="reports-tab-category">Category Performance</TabsTrigger>
          <TabsTrigger value="pm" data-testid="reports-tab-pm">PM Scorecard</TabsTrigger>
        </TabsList>

        <TabsContent value="category" className="space-y-6">
          <Card className="rounded-sm">
            <CardContent className="p-4">
              <Select value={catPmDays} onValueChange={setCatPmDays}>
                <SelectTrigger className="w-48 rounded-sm" data-testid="category-perf-days-select">
                  <SelectValue placeholder="Time period" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">Last 7 days</SelectItem>
                  <SelectItem value="30">Last 30 days</SelectItem>
                  <SelectItem value="90">Last 90 days</SelectItem>
                  <SelectItem value="365">Last 12 months</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-2">Based on SUBMITTED Bid Engine results only (the official record) — target vs actual price achieved, bid volume and win rate, by category.</p>
            </CardContent>
          </Card>
          <Card className="rounded-sm">
            <CardHeader className="pb-3"><CardTitle className="text-lg flex items-center gap-2"><Target className="h-4 w-4" />Target vs Actual, by Category</CardTitle></CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="font-medium text-xs uppercase">Category</TableHead>
                    <TableHead className="font-medium text-xs uppercase text-right">Bids</TableHead>
                    <TableHead className="font-medium text-xs uppercase text-right">Win Rate</TableHead>
                    <TableHead className="font-medium text-xs uppercase text-right">Avg Target</TableHead>
                    <TableHead className="font-medium text-xs uppercase text-right">Avg Actual</TableHead>
                    <TableHead className="font-medium text-xs uppercase text-right">Total Value</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow><TableCell colSpan={6} className="text-center py-8"><Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" /></TableCell></TableRow>
                  ) : catPmData.by_category.length === 0 ? (
                    <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No submitted bids in this period</TableCell></TableRow>
                  ) : catPmData.by_category.map((c) => (
                    <TableRow key={c.category} className="table-row-hover" data-testid={`category-perf-row-${c.category}`}>
                      <TableCell className="font-medium">{c.category}</TableCell>
                      <TableCell className="font-mono text-right">{c.bid_count}</TableCell>
                      <TableCell className="text-right">
                        <Badge variant="outline" className={c.win_rate_pct >= 50 ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}>{c.win_rate_pct}%</Badge>
                      </TableCell>
                      <TableCell className="font-mono text-right">{c.avg_target_price != null ? formatCurrency(c.avg_target_price) : '—'}</TableCell>
                      <TableCell className="font-mono text-right">{c.avg_actual_price != null ? formatCurrency(c.avg_actual_price) : '—'}</TableCell>
                      <TableCell className="font-mono text-right">{formatCurrency(c.total_value_negotiated)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="pm" className="space-y-6">
          <Card className="rounded-sm">
            <CardHeader className="pb-3"><CardTitle className="text-lg flex items-center gap-2"><Award className="h-4 w-4" />PM Scorecard (Last {catPmDays} Days)</CardTitle></CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="font-medium text-xs uppercase">Product Manager</TableHead>
                    <TableHead className="font-medium text-xs uppercase text-right">Total Bids</TableHead>
                    <TableHead className="font-medium text-xs uppercase text-right">Targets Met</TableHead>
                    <TableHead className="font-medium text-xs uppercase text-right">Negotiate</TableHead>
                    <TableHead className="font-medium text-xs uppercase text-right">Missed</TableHead>
                    <TableHead className="font-medium text-xs uppercase text-right">Met Rate</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow><TableCell colSpan={6} className="text-center py-8"><Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" /></TableCell></TableRow>
                  ) : catPmData.by_pm.length === 0 ? (
                    <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No submitted bids with an assigned PM in this period</TableCell></TableRow>
                  ) : catPmData.by_pm.map((p) => (
                    <TableRow key={p.pm_name} className="table-row-hover" data-testid={`pm-scorecard-row-${p.pm_name}`}>
                      <TableCell className="font-medium">{p.pm_name}</TableCell>
                      <TableCell className="font-mono text-right">{p.bid_count}</TableCell>
                      <TableCell className="font-mono text-right text-emerald-700">{p.buy_count}</TableCell>
                      <TableCell className="font-mono text-right text-amber-700">{p.negotiate_count}</TableCell>
                      <TableCell className="font-mono text-right text-red-700">{p.reject_count}</TableCell>
                      <TableCell className="text-right">
                        <Badge variant="outline" className={p.win_rate_pct >= 50 ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}>{p.win_rate_pct}%</Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
